import type { CSSProperties } from 'react';

import type { WithdrawBalanceType } from '../../components/FullPageDialogs/RedeemDialog/types';
import type { PocSituation, PocSituationStatus } from '../../features/poc/types';
import type { PaymentType, TransactionType } from '../../Payments/v2/src/enums';
import type { PlatformEnum } from '../../services/platformContext/platformContextTypes';
import type { SystemErrorCodes } from '../app/types';
import type { BundleItem } from '../rewards/types';

export type CardTypeId =
  | 'amex'
  | 'americanExpress'
  | 'visa'
  | 'mastercard'
  | 'masterCard'
  | 'discover'
  | 'diners'
  | 'dinersclub'
  | 'jcb'
  | 'unionpay';

export const enum TSafeChargeMethodType {
  card = 'cc_card',
  bank = 'apmgw_eCheckSelect',
  applepay = 'ppp_ApplePay',
  skrill = 'skrill',
  bankPaysafe = 'PAY_BY_BANK',
  payouts = 'PAYOUTS',
  interac = 'INTERAC_ETRANSFER',
  sepa = 'SEPA',
  paysafeCard = 'PAYSAFECARD',
}

export enum EnumPaymentMethods {
  card = 'CARD',
  applePay = 'APPLEPAY',
  APPLE_PAY = 'APPLE_PAY',
  skrill = 'SKRILL',
  bank = 'apmgw_eCheckSelect',
  interac = 'INTERAC',
}

export interface UserTransaction {
  transactionId: string;
  status: number;
  playerId: string;
  amount?: number;
  isFirstDeposit: boolean;
  paymentMethod: EnumPaymentMethods;
  rememberCardDetails: boolean;
  description?: string;
  providerError?: number;
  error?: SystemErrorCodes;
  avsCheckFailed?: boolean;
  actualGrantedRewards?: {
    gcCoin: number;
    scCoin: number;
    bundleItems: BundleItem[];
  };
  [key: string]: unknown;
}

export const enum TransactionStatus {
  PENDING = 0,
  SUCCESS = 1,
  REJECTED = 2,
  CANCELED = 3,
  ROLLBACK = 4,
  APPROVED = 5,
  FAILED = 6,
  INPROGRESS = 7,
  POSTPONE = 8,
}

export const enum RedeemTransactionStatus {
  PENDING = 0,
  APPROVED = 1,
  REJECTED = 2,
  CANCELED = 3,
  FAILED = 6,
  INPROGRESS = 7,
  ACTIVATION = 18,
  EXPIRED = 19,
}

export const enum TRedeemAnalyticsType {
  card = 'credit_card',
  applepay = 'apple',
  skrill = 'skrill',
  bank = 'IBT',
  bankPaysafe = 'bank_paysafe',
  payouts = 'payouts',
  interac = 'interac',
  sepa = 'SEPA',
  paysafeCard = 'paysafe_card',
}

export const enum TSafeChargePaymentType {
  redeem = 'redeem',
  deposit = 'deposit',
}

export const enum PaymentProvider {
  paysafe = 'paysafe',
  nuvei = 'nuvei',
}

export interface ISafeChargePaymentMethod {
  paymentMethod: string;
}

export interface IConfirmRedeemSafeChargeData {
  userPMId: number;
  ipAddress: string;
  paymentMethodType: TSafeChargeMethodType;
  paymentType: TSafeChargePaymentType;
  platform?: PlatformEnum;
  withdrawBalanceType: WithdrawBalanceType;
}

export interface ISendConfirmRedeemSafeChargeData extends IConfirmRedeemSafeChargeData {
  amount: number;
}

export interface IPayoutsRedeemRequest {
  coins: number;
  paymentMethod: TSafeChargeMethodType;
  platform?: PlatformEnum;
  paymentType: TSafeChargePaymentType;
  otp?: string;
  withdrawBalanceType: WithdrawBalanceType;
}

export interface IUpoCard {
  ccCardNumber: string;
  ccExpMonth: string;
  ccExpYear: string;
  ccNameOnCard: string;
}

export interface IUpoDataAddress {
  firstName: string;
  lastName: string;
  countryCode: string;
  email: string;
}

export interface IUpoDataCard {
  userPaymentOptionId: number;
  upoData: IUpoCard;
}

export interface IUpoDataCardRedeem {
  cardType: string;
  firstSix: string;
  lastFour: string;
  userPaymentHandle: string;
}

export type BankAccountCoreData = {
  account: string;
  id: string;
  registrationId?: string;
  uniqueUpoId: string;
  isInUse?: boolean;
};

export interface IBankAccountRedeem {
  bankAccounts: (BankAccountCoreData & {
    paymentHandleToken: string;
  })[];
}

export interface IPaysafeBankRedirectRedeem {
  account: string;
  id: string;
  paymentHandleToken: string;
}

export interface IUpoBankAccount {
  mazooma_fi_routing: string;
  accountID: string;
  mazoomaFiAcc: string;
  mazooma_fi_account_type: string;
  buyerName: string;
  mazoomaFiName: string;
  mazooma_fi_account_number: string;
}

export interface IUpoDataBankAccount {
  userPaymentOptionId: number;
  billingAddress: IUpoDataAddress;
  upoData: IUpoBankAccount;
}

export interface IUpoData {
  card: IUpoDataCard | undefined;
  bankAccount: IUpoDataBankAccount | undefined;
}

export const analyticsRedeemMethod: Record<TSafeChargeMethodType, TRedeemAnalyticsType> = {
  [TSafeChargeMethodType.bank]: TRedeemAnalyticsType.bank,
  [TSafeChargeMethodType.bankPaysafe]: TRedeemAnalyticsType.bankPaysafe,
  [TSafeChargeMethodType.card]: TRedeemAnalyticsType.card,
  [TSafeChargeMethodType.applepay]: TRedeemAnalyticsType.applepay,
  [TSafeChargeMethodType.skrill]: TRedeemAnalyticsType.skrill,
  [TSafeChargeMethodType.payouts]: TRedeemAnalyticsType.payouts,
  [TSafeChargeMethodType.interac]: TRedeemAnalyticsType.interac,
  [TSafeChargeMethodType.sepa]: TRedeemAnalyticsType.sepa,
  [TSafeChargeMethodType.paysafeCard]: TRedeemAnalyticsType.paysafeCard,
};

export interface INewCardBillingDetails {
  address: string;
  city: string;
  territoryKey: string;
  zipCode: string;
}

export interface ISavedCardData {
  lastDigits: string;
  cardExpiry: {
    month: string;
    year: string;
  };
  cardBin: string;
  cardType: string;
  status: string;
  cardCategory: string;
  holderName: string;
}

export interface ISavedCreditCardData {
  paymentHandleId: string;
  paymentHandleToken: string;
  card: ISavedCardData;
  status: string;
  customerToken: string;
  timeToLiveSeconds: number;
  timeToLiveDate?: number;
  merchantRefNum: string;
}

export interface IPreviousPaymentData {
  provider: string;
  method: EnumPaymentMethods;
  paymentHandles: ISavedCreditCardData[];
}

export interface IPaymentCardPayload {
  expMonth: string;
  expYear: string;
  firstName: string;
  lastName: string;
}

export interface IUserPaymentTokenData extends TokenExTokenizeResponse, INewCardBillingDetails, IPaymentCardPayload {}

export type ILastPaymentMethodDetails = IUserPaymentTokenData | ISavedCreditCardData;

export interface IBaseLastPaymentMethod {
  method: EnumPaymentMethods;
  provider: string;
}

export interface ILastPaymentMethodCard extends IBaseLastPaymentMethod {
  details: IUserPaymentTokenData;
}

export interface ILastPaymentMethodSkrill extends IBaseLastPaymentMethod {
  details: ISavedCreditCardData;
}

export interface ILastPaymentMethod extends IBaseLastPaymentMethod {
  details: ILastPaymentMethodDetails;
}

export interface PurchaseTokenExPayloadArg extends TokenExTokenizeResponse, Partial<INewCardBillingDetails> {}

export type ISafeChargeTempToken = {
  internalRequestId: number;
  status: 'ERROR' | 'SUCCESS';
  errCode: number;
  reason: string;
  sessionToken: string;
  ccTempToken: string;
  error: {
    id?: string;
    code: number;
    message: string;
  };
};

export interface PurchaseSafeChargeTempTokenPayloadArg extends ISafeChargeTempToken, Partial<INewCardBillingDetails> {}

export type IPaysafeTempToken = {
  token: string;
};

export interface PurchasePaysafeTempTokenPayloadArg extends IPaysafeTempToken, Partial<INewCardBillingDetails> {}

export interface IUserLastPaymentTokenResponse {
  lastPaymentMethod: ILastPaymentMethod;
}

export interface IRedeemTransaction {
  withdrawRequestId: number;
  status: RedeemTransactionStatus;
  amount: number;
  paymentProvider: string;
  isProcess: boolean | undefined;
  acknowledged?: boolean;
  createdAt: string;
  coinType?: WithdrawBalanceType;
  [key: string]: unknown;
}

export type PaysafeDetectedCardBrand =
  | 'Visa'
  | 'Mastercard'
  | 'American Express'
  | 'Discover'
  | 'Diners Club'
  | 'JCB'
  | 'Maestro';

export type IInitPaysafeInstance = {
  show: () => void;
  complete: (status: string) => void;
  fields: {
    setup: <T>(key: string, option: T) => Promise<any>;
  };
  tokenize: (arg0: {
    transactionType: string | TransactionType;
    amount: number;
    applePay?: { country: string };
    merchantRefNum: string;
    paymentType: string | PaymentType;
    customerDetails?: {
      holderName?: string;
      billingDetails?: any;
      profile?: any;
    };
  }) => Promise<any>;

  on(
    eventName: 'CardBrandRecognition',
    listener: (instance: IInitPaysafeInstance, event?: { data?: { cardBrand?: PaysafeDetectedCardBrand } }) => void,
  ): void;
};

export type IInitPaysafeCreditCardInstance = {
  fields: any;
  tokenize: (arg0: {
    transactionType: string;
    amount: number;
    merchantRefNum: any;
    customerDetails: {
      billingDetails: { zip: any; country: any; city: any; state: string | undefined };
      profile: { firstName: any; lastName: any; email: string };
    };
    paymentType: string;
  }) => Promise<any>;
} | null;

export interface IAccountCaptureResponseCbArgs {
  status: string;
  redirectUrl: string;
  sessionToken: string;
  userTokenId: string;
  internalRequestId: string;
  errCode: string;
  reason: string;
  merchantId: string;
  merchantSiteId: string;
  version: string;
}

export type SafeChargeFieldsCfg = Partial<{
  fontSize: `${number}${'px' | 'em' | 'rem' | '%' | 'pt'}`;
  locale: string;
  textDirection: 'ltr' | 'rtl';
  fonts: { cssUrl: string }[];
}>;

export type SafeChargeSingleFieldStyle = Partial<
  Record<'base' | 'valid' | 'invalid' | 'empty', CSSProperties & { '::placeholder'?: CSSProperties }>
>;

export type SafeChargeSingleFieldCfg = Partial<{
  style: SafeChargeSingleFieldStyle;
  card_brand: CardTypeId;
}>;

export type SafeChargeSingleField = {
  destroy: VoidFunction;
  on: (eventName: string, listener: (event?: { cardType?: CardTypeId }) => void) => void;
  attach: (selector: string) => void;
};

export type SafeChargeFields = {
  create: (fieldName: string, config?: SafeChargeSingleFieldCfg) => SafeChargeSingleField;
};

export interface IInitSafeChargeInstance {
  createApplePayPayment: (data: any, call: (data: { result: string; status: string }) => void) => void;
  createPayment: (data: any, call: (data: { result: string; transactionStatus: string }) => void) => void;
  addCardUpo: (data: any, call: (data: { result: string; status: string }) => void) => void;
  getApms: (data: any, call: (data: { result: string; status: string }) => void) => void;
  accountCapture: (data: any, call: (data: IAccountCaptureResponseCbArgs) => void) => void;
  getToken: (card: any, data: any) => Promise<ISafeChargeTempToken>;
  fields: (fieldsConfig: SafeChargeFieldsCfg) => SafeChargeFields;
}

export interface IRedeemFailure {
  isFailed: boolean;
  options?: {
    [key: string]: Record<string, unknown> | string | number | boolean | null;
  };
}

export interface ICheckForceKYC {
  isForceKYC: boolean;
  isProcessForKYC: boolean;
  isSubscription: boolean;
}

export interface ITokenExFormErrors {
  cc: null | string;
  cvv: null | string;
  expiryDate: null | string;
  nameOnCard: null | string;
}

export type UcvStatus = 'REQUIRED' | 'NOT_REQUIRED' | 'FAILED' | 'UNKNOWN';

export type CvvErrorListener = (arg: {
  paymentMethod: EnumPaymentMethods;
  errorCode: number;
  errorDetails: string;
  pendingTransactionParams: string;
  transactionId: string;
}) => void;

export interface IPackageData {
  packageId?: number;
}
export interface IBillingAddress {
  zip?: string;
  street?: string;
  city?: string;
  territoryKey?: string;
}

export interface IValidateMerchantRequest {
  validationURL: string;
}

export interface IPaymentAuthorizeRequest {
  payload: {
    applePay: ApplePayPayment;
  };
  billingAddress: IBillingAddress;
  packageData: IPackageData;
  platform: PlatformEnum;
  isRetry?: boolean;
}

type PayloadByRedemptionPreparationStatus = {
  failed: { reason: string };
  validityIssue: PocSituation<Exclude<PocSituationStatus, 'fullyValid'>>;
  success: { scheduledForAutomation: boolean };
  statusMismatch: { expected: number; actual: number };
};

type RedemptionPreparationStatus = keyof PayloadByRedemptionPreparationStatus;

type RedemptionPreparationResult<StatusPool extends RedemptionPreparationStatus = RedemptionPreparationStatus> = {
  [S in StatusPool]: { status: S; payload: PayloadByRedemptionPreparationStatus[S] };
}[StatusPool];

export type ApiRedemptionCaptureResult = {
  preparationResult: RedemptionPreparationResult;
  redemptionRequestId: number;
};

export type RedemptionCaptureResult = ApiRedemptionCaptureResult & {
  redemptionAmount: number;
};

export interface IConfirmRedeemResponse {
  transactionId: number;
  status: 'pending' | 'success' | 'failed';
  extra?: { isOtpRequired: boolean; isOtpInvalid: boolean; isSendOtpError: boolean };
}

export type UPODeletionResult = 'success' | 'error' | 'notPerformed' | 'noMatchingProvider';

export interface ISelectedWinForRedeem {
  id: number;
  amount: number;
}
